import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Space, Spin, Typography } from 'antd';
import { useDispatch, useIntl } from 'umi';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { BranchModel, ServiceAvailability, ServiceModel, ServicePeriod } from '@/typings/models/Location';
import {
  getLocationAllowDateTimes,
  getLocationBlockPeriods,
  getLocationOpenPeriods,
  transformToServerFormat,
  transformToSlotsFormat,
} from '@/utils/location';
import { getDays } from '@/utils/openHours';
import OpenHours from '@/components/ResourceSettings/OpenHours';
import { PeriodFormUI } from '@/components/ResourceSettings/PeriodFormUI';
import { updateServiceRequest } from '@/services/api/location';
import { processAPIResponseNow } from '@/utils/request';
import { F } from '@/utils/intl';

const { Title, Text } = Typography;

interface ServiceOperatingHoursProps {
  service: ServiceModel;
  branch: BranchModel;
  onSuccess?: (data?: any) => void;
  branchLoading?: boolean;
}

const ServiceOperatingHours: React.FC<ServiceOperatingHoursProps> = ({ service, branch, onSuccess, branchLoading }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const defaultDays = useMemo(() => getDays(intl), [intl]);
  const [regularHours, setRegularHours] = useState<any[]>(defaultDays);
  const prevServiceId = React.useRef(service?.id);

  useEffect(() => {
    if (!service || isEmpty(service)) return;

    // If we are re-fetching the branch, don't reset to defaults if we have old data
    // to avoid flickering the UI from set hours to empty hours and back.
    const availability = getLocationAllowDateTimes(service);
    const hasIncompleteSlots = regularHours.some(
      (day) => day.enabled && day.slots.some((slot) => !slot.start || !slot.end),
    );

    const isDifferentService = prevServiceId.current !== service.id;

    if (isDifferentService || (!hasIncompleteSlots && !branchLoading)) {
      if (!availability?.length) {
        setRegularHours(defaultDays);
      } else {
        setRegularHours(transformToSlotsFormat(availability, intl));
      }
    }

    prevServiceId.current = service.id;
  }, [service, defaultDays, intl, branchLoading]);

  const handleUpdateAvailability = async (newAvailability: ServiceAvailability[]) => {
    setLoading(true);
    const payload = {
      calendar: {
        ...(service.calendar || {}),
        Availability: newAvailability,
      },
    };
    const Response = await updateServiceRequest({
      params: { serviceId: service.id },
      payload,
    });

    processAPIResponseNow(Response, {
      onSuccess: (data) => {
        onSuccess?.(data?.data || payload);
      },
      onEnd: () => {
        setLoading(false);
      },
    });
  };

  const handleRegularHoursChange = (newData: any[]) => {
    setRegularHours(newData);

    // Skip API update if any enabled day has incomplete slots
    const hasIncompleteSlots = newData.some(
      (day) => day.enabled && day.slots.some((slot) => !slot.start || !slot.end),
    );

    if (hasIncompleteSlots) {
      return;
    }

    const updatedRegularAvailability = newData
      .filter((day) => day.enabled)
      .map((day) => {
        const slots = day.slots
          .filter((slot) => slot.start && slot.end)
          .map((slot) => ({
            start: dayjs(slot.start, 'HH:mm').format('HH:mm'),
            end: dayjs(slot.end, 'HH:mm').format('HH:mm'),
          }));
        return slots.length
          ? { allow_day_and_time: { day: day.day.toLowerCase(), slots } }
          : null;
      })
      .filter(Boolean);

    const transformedRegular = transformToServerFormat(updatedRegularAvailability, intl);

    // Preserve exceptions (block and open periods)
    const blockPeriods = (service.calendar?.Availability || [])
        .filter(a => a.block_period)
        .map(a => ({ block_period: a.block_period }));
    const openPeriods = (service.calendar?.Availability || [])
        .filter(a => a.allow_period)
        .map(a => ({ allow_period: a.allow_period }));

    const newAvailability = [...transformedRegular, ...blockPeriods, ...openPeriods];
    handleUpdateAvailability(newAvailability);
  };

  const handleExceptionChange = (updatedPeriods: ServicePeriod[], mode: 'block' | 'open') => {
    const regularAvailability = getLocationAllowDateTimes(service);

    const transformedExceptions = updatedPeriods.map(p => ({
      [mode === 'block' ? 'block_period' : 'allow_period']: {
        start: dayjs(p.start).format('YYYY-MM-DD[T]HH:mm:ss'),
        end: dayjs(p.end).format('YYYY-MM-DD[T]HH:mm:ss'),
      },
    }));

    const otherExceptions = mode === 'block'
      ? getLocationOpenPeriods(service).map(p => ({ allow_period: { start: p.start, end: p.end } }))
      : getLocationBlockPeriods(service).map(p => ({ block_period: { start: p.start, end: p.end } }));

    const newAvailability = [...regularAvailability, ...otherExceptions, ...transformedExceptions];
    handleUpdateAvailability(newAvailability);
  };

  return (
    <Spin spinning={loading}>
      <Space vertical size={8} style={{ width: '100%' }}>
        <OpenHours
          data={regularHours}
          onChange={handleRegularHoursChange}
          supportMultipleEntries={true}
        />

        <Divider size={'small'} />

        <div>
          <Title level={5}>
            <F
              id="pages.locations.additionalOpenHours"
              defaultMessage="Additional Open Hours"
            />
          </Title>
          <PeriodFormUI
            mode="open"
            periods={getLocationOpenPeriods(service)}
            onAdd={(period) =>
              handleExceptionChange(
                [...getLocationOpenPeriods(service), period],
                'open',
              )
            }
            onChange={(periods) => handleExceptionChange(periods, 'open')}
          />
        </div>

        <Divider size={'small'} />

        <div>
          <Title level={5}>
            <F
              id="component.locationDrawer.blockHours"
              defaultMessage="Block Periods"
            />
          </Title>
          <PeriodFormUI
            mode="block"
            periods={getLocationBlockPeriods(service)}
            onAdd={(period) =>
              handleExceptionChange(
                [...getLocationBlockPeriods(service), period],
                'block',
              )
            }
            onChange={(periods) => handleExceptionChange(periods, 'block')}
          />
        </div>
      </Space>
    </Spin>
  );
};

export default ServiceOperatingHours;
